import React from 'react'

import {extractNarrativeNameList} from '../../../../modes/attendance/attendanceNameList'
import {
  hasPackedNameLists,
  hasStructuredAttendanceView,
} from '../../../../modes/attendance/attendanceV2ResultTable'
import {CompoundPart} from '../../../../modes/attendance/attendanceV2Types'
import {condenseAttendanceNarrative} from '../../../../modes/attendance/condenseAttendanceNarrative'
import AttendanceV2AnswerBody from './AttendanceV2AnswerBody'

type AttendanceV2CompoundViewProps = {
  parts: CompoundPart[]
  theme: 'light' | 'dark'
  renderHtmlBody: (text: string, ref?: React.RefObject<HTMLDivElement | null>) => React.ReactNode
  narrativeRef?: React.RefObject<HTMLDivElement | null>
}

/** A gap part's narrative *is* its gap text, which the notice inside the answer body renders. */
function isGapPart(part: CompoundPart): boolean {
  return part.kind === 'gap' || Boolean(part.gap)
}

function narrativeOf(part: CompoundPart): string {
  if (isGapPart(part)) return ''
  const raw = part.narrative ?? ''
  // The part's own result view draws its packed name lists, so the sentence repeating them goes.
  const text = hasPackedNameLists(part) ? (extractNarrativeNameList(raw)?.text ?? raw) : raw
  return condenseAttendanceNarrative(text, {
    hasStructuredView: hasStructuredAttendanceView(part),
  })
}

/** Renders a `compound` answer — one multi-part question — as a numbered stack of sections, each
 *  headed by the sub-request it answers and rendered by the same views a standalone answer uses.
 *  Without this the whole answer collapses to prose: a compound payload keeps its data under
 *  `parts[]`, and every structured reader looks at `data`, which is null on the container. */
const AttendanceV2CompoundView: React.FC<AttendanceV2CompoundViewProps> = ({
  parts,
  theme,
  renderHtmlBody,
  narrativeRef,
}) => {
  if (!parts.length) return null

  const narratives = parts.map(narrativeOf)
  // The bubble's theme-colour pass only reaches nodes under this ref, so hand it to the first
  // narrative we actually render.
  const refIndex = narratives.findIndex(Boolean)

  const isDark = theme === 'dark'
  const headingColor = isDark ? '#ffffff' : '#212529'
  const dividerColor = isDark ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.08)'
  // The sub-request heading and the answer prose beneath it are both plain sentences, so without a
  // band + badge they read as one continuous paragraph and the section boundaries disappear.
  const headerBg = isDark ? 'rgba(147,197,253,0.12)' : 'rgba(37,99,235,0.07)'
  const accent = isDark ? '#93c5fd' : '#2563eb'
  const badgeText = isDark ? '#0f172a' : '#ffffff'
  const showNumbers = parts.length > 1

  return (
    <div>
      {parts.map((part, idx) => {
        const narrative = narratives[idx]
        const title = part.requestText?.trim()

        return (
          <div
            key={part.index ?? idx}
            className={idx > 0 ? 'mt-3 pt-3' : undefined}
            style={idx > 0 ? {borderTop: `1px solid ${dividerColor}`} : undefined}
          >
            {title ? (
              <div
                className='d-flex align-items-start gap-2 mb-2 px-2 py-2 rounded-2'
                style={{backgroundColor: headerBg, borderLeft: `3px solid ${accent}`}}
              >
                {showNumbers ? (
                  <span
                    className='d-inline-flex align-items-center justify-content-center flex-shrink-0 fw-bold'
                    style={{
                      width: '1.15rem',
                      height: '1.15rem',
                      marginTop: '0.05rem',
                      borderRadius: '50%',
                      backgroundColor: accent,
                      color: badgeText,
                      fontSize: '0.7rem',
                      lineHeight: 1,
                    }}
                  >
                    {idx + 1}
                  </span>
                ) : null}
                <span
                  className='fw-bold'
                  style={{fontSize: '0.82rem', color: headingColor, lineHeight: 1.35}}
                >
                  {title}
                </span>
              </div>
            ) : null}
            {narrative
              ? renderHtmlBody(narrative, idx === refIndex ? narrativeRef : undefined)
              : null}
            <AttendanceV2AnswerBody payload={part} theme={theme} />
          </div>
        )
      })}
    </div>
  )
}

export default AttendanceV2CompoundView
